import { match, P } from 'ts-pattern'
import { z } from 'zod'
import { useSSEStream } from '../../hooks/use-sse-stream.ts'
import { buildPath } from '../../utils/build-path.ts'

// Hand-maintained mirror of DocumentPhase in analysis-service
// (src/pixee_analysis_service/coding_guidelines/processing.py) — the events
// endpoint has no OpenAPI payload schema.
export const CODING_GUIDELINES_DOCUMENT_FAILED_PHASES = [
  'failed_extract_password',
  'failed_extract_scan',
  'failed_extract_corrupt',
  'failed_processing',
] as const

export const CodingGuidelinesDocumentFailedPhaseSchema = z.enum(CODING_GUIDELINES_DOCUMENT_FAILED_PHASES)

const CodingGuidelinesDocumentProgressEventSchema = z.object({
  phase: z.enum(['received', 'extracting', 'indexing', 'ready']),
  created_at: z.string(),
})

// Terminal failure events embed the backend's problem-JSON; `detail` carries
// the document's failure_reason (or a backend default) for direct display.
const CodingGuidelinesDocumentFailureEventSchema = z.object({
  phase: CodingGuidelinesDocumentFailedPhaseSchema,
  created_at: z.string(),
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
})

export const CodingGuidelinesDocumentEventSchema = z.discriminatedUnion('phase', [
  CodingGuidelinesDocumentProgressEventSchema,
  CodingGuidelinesDocumentFailureEventSchema,
])

export type CodingGuidelinesDocumentEvent = z.infer<typeof CodingGuidelinesDocumentEventSchema>

const isTerminalPhase = (event: CodingGuidelinesDocumentEvent): boolean =>
  match(event.phase)
    .with(P.union('received', 'extracting', 'indexing'), () => false)
    .with(P.union('ready', ...CODING_GUIDELINES_DOCUMENT_FAILED_PHASES), () => true)
    .exhaustive()

type UseCodingGuidelinesDocumentEventsOptions = {
  organizationId?: string
  documentId: string | undefined
  onEvent?: (event: CodingGuidelinesDocumentEvent) => unknown
}

export const useCodingGuidelinesDocumentEvents = ({
  organizationId = 'default-organization',
  documentId,
  onEvent,
}: UseCodingGuidelinesDocumentEventsOptions) => {
  const url =
    documentId !== undefined
      ? buildPath('/api/v1/organizations/:organizationId/coding-guidelines/:id/events', {
          organizationId,
          id: documentId,
        })
      : undefined

  return useSSEStream({
    url,
    schema: CodingGuidelinesDocumentEventSchema,
    isFinished: isTerminalPhase,
    onEvent,
  })
}
